import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from '@mui/material';

import { usePedestrianLayerTypeEnabled } from '../../../hooks/useIdlc1129Features';
import { LAYER_TYPES } from '../../project-settings/types';
import { DXFPreview2D } from './DXFPreview2D';
import { create3DMeshes } from './create3DMeshes';
import { exportToGLB } from './exportToGLB';
import { DXFData, DXFLayerConfig, DXFLayerType } from './types';

// Map existing project layer types to DXF layer types with heights
const DXF_LAYER_TYPES: DXFLayerType[] = (Object.keys(LAYER_TYPES) as Array<keyof typeof LAYER_TYPES>).map((key) => {
  const heights: Record<string, number> = {
    Unspecified: 1,
    Sidewalk: 0.12,
    Pedestrian: 0.12,
    Road: 0.08,
    Environment: 0.3,
    Building: 12,
  };
  return {
    id: key,
    label: key,
    height: heights[key] || 1,
    color: LAYER_TYPES[key],
  };
});

interface DXFImportDialogProps {
  open: boolean;
  onClose: () => void;
  parsedData: DXFData;
  onGenerate: (objBlob: Blob, fileName: string) => void;
  originalFileName: string;
}

export const DXFImportDialog: React.FC<DXFImportDialogProps> = ({
  open,
  onClose,
  parsedData,
  onGenerate,
  originalFileName,
}) => {
  const { t } = useTranslation();
  const pedestrianLayerType = usePedestrianLayerTypeEnabled();

  const [layerConfig, setLayerConfig] = useState<Record<string, DXFLayerConfig>>(() => {
    const config: Record<string, DXFLayerConfig> = {};
    parsedData.layers.forEach((l) => {
      config[l] = { enabled: true, type: 'Building', height: 12, color: LAYER_TYPES.Building };
    });
    return config;
  });

  const [isGenerating, setIsGenerating] = useState(false);

  const updateLayer = useCallback((layer: string, key: keyof DXFLayerConfig, value: boolean | string | number) => {
    setLayerConfig((prev) => ({ ...prev, [layer]: { ...prev[layer], [key]: value } }));
  }, []);

  const setLayerType = useCallback((layer: string, typeId: string) => {
    const layerType = DXF_LAYER_TYPES.find((x) => x.id === typeId);
    if (layerType) {
      setLayerConfig((prev) => ({
        ...prev,
        [layer]: { ...prev[layer], type: typeId, height: layerType.height, color: layerType.color },
      }));
    }
  }, []);

  const toggleAll = useCallback(() => {
    const allEnabled = Object.values(layerConfig).every((c) => c.enabled);
    setLayerConfig((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[k] = { ...next[k], enabled: !allEnabled };
      });
      return next;
    });
  }, [layerConfig]);

  const enabledCount = useMemo(() => Object.values(layerConfig).filter((c) => c.enabled).length, [layerConfig]);

  const entityCount = useMemo(
    () => parsedData.entities.filter((e) => layerConfig[e.layer]?.enabled).length,
    [parsedData.entities, layerConfig],
  );

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      const meshes = create3DMeshes(parsedData.entities, layerConfig);
      if (meshes.length === 0) {
        setIsGenerating(false);
        return;
      }
      const blob = await exportToGLB(meshes);
      const glbFileName = originalFileName.replace(/\.dxf$/i, '') + '.glb';
      onGenerate(blob, glbFileName);
    } catch (err) {
      console.error('DXF 3D generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [parsedData.entities, layerConfig, originalFileName, onGenerate]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#12141a',
          color: '#e4e6ed',
          maxWidth: '1200px',
          height: '80vh',
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div>
            <h2 className="text-base font-semibold text-white">{t('projects.newProject.dxf.configTitle')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {parsedData.layers.length} {t('projects.newProject.dxf.layersFound')} &middot; {enabledCount}{' '}
              {t('projects.newProject.dxf.active')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Layer config */}
          <div className="w-[400px] flex flex-col border-r border-white/10 bg-[#13151c]">
            {/* Toggle all */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="text-xs text-gray-400 uppercase tracking-wider">
                {t('projects.newProject.dxf.layers')}
              </span>
              <button
                onClick={toggleAll}
                className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded border border-white/10 transition"
              >
                {Object.values(layerConfig).every((c) => c.enabled)
                  ? t('projects.newProject.dxf.deselectAll')
                  : t('projects.newProject.dxf.selectAll')}
              </button>
            </div>

            {/* Layer list */}
            <div className="flex-1 overflow-y-auto px-3 py-2 custom-scrollbar">
              {parsedData.layers.map((layer) => {
                const cfg = layerConfig[layer];
                if (!cfg) return null;
                const entCount = parsedData.entities.filter((e) => e.layer === layer).length;

                return (
                  <div
                    key={layer}
                    className={`mb-2 rounded-lg border transition ${
                      cfg.enabled ? 'border-white/10 bg-white/[0.02]' : 'border-white/5 bg-black/20 opacity-50'
                    }`}
                  >
                    {/* Layer header */}
                    <div className="flex items-center gap-2.5 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={cfg.enabled}
                        onChange={(e) => updateLayer(layer, 'enabled', e.target.checked)}
                        className="w-3.5 h-3.5 accent-lime-400 cursor-pointer"
                      />
                      <div className="w-3 h-3 rounded-sm shrink-0" style={{ background: cfg.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-white truncate">{layer}</div>
                        <div className="text-[10px] text-gray-500">
                          {entCount} {t('projects.newProject.dxf.entities')}
                        </div>
                      </div>
                    </div>

                    {/* Layer controls */}
                    {cfg.enabled && (
                      <div className="px-3 pb-3 pt-1 border-t border-white/5 flex gap-2 flex-wrap">
                        <div className="flex-1 min-w-[120px]">
                          <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                            {t('projects.newProject.dxf.type')}
                          </label>
                          <select
                            value={cfg.type}
                            onChange={(e) => setLayerType(layer, e.target.value)}
                            className="w-full px-2 py-1.5 rounded-md border border-white/10 bg-[#1a1d24] text-white text-xs outline-none cursor-pointer"
                          >
                            {DXF_LAYER_TYPES.filter(
                              (lt) => lt.id !== 'Pedestrian' || pedestrianLayerType || cfg.type === 'Pedestrian',
                            ).map((lt) => (
                              <option key={lt.id} value={lt.id}>
                                {t(`projects.newProject.dxf.layerType.${lt.id}`)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="w-[80px]">
                          <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                            {t('projects.newProject.dxf.heightM')}
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            min="0.01"
                            value={cfg.height}
                            onChange={(e) => updateLayer(layer, 'height', parseFloat(e.target.value) || 0.1)}
                            className="w-full px-2 py-1.5 rounded-md border border-white/10 bg-[#1a1d24] text-white text-xs outline-none text-center"
                          />
                        </div>

                        <div className="w-[44px]">
                          <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                            {t('projects.newProject.dxf.color')}
                          </label>
                          <input
                            type="color"
                            value={cfg.color}
                            onChange={(e) => updateLayer(layer, 'color', e.target.value)}
                            className="w-full h-[30px] rounded-md border border-white/10 bg-transparent cursor-pointer p-0.5"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Generate button */}
            <div className="px-4 py-3 border-t border-white/10">
              <button
                onClick={handleGenerate}
                disabled={enabledCount === 0 || isGenerating}
                className={`w-full py-2.5 rounded-lg text-xs font-bold tracking-wide transition ${
                  enabledCount > 0 && !isGenerating
                    ? 'bg-lime-400 text-black hover:bg-lime-500'
                    : 'bg-white/10 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isGenerating ? t('projects.newProject.dxf.generating') : t('projects.newProject.dxf.generate3D')}
              </button>
              <div className="text-[10px] text-gray-500 text-center mt-1.5">
                {enabledCount} {t('projects.newProject.dxf.layers')} &middot; {entityCount}{' '}
                {t('projects.newProject.dxf.entities')}
              </div>
            </div>
          </div>

          {/* Right: 2D Preview */}
          <div className="flex-1 bg-[#0f1117]">
            <DXFPreview2D entities={parsedData.entities} layerConfig={layerConfig} />
          </div>
        </div>
      </div>
    </Dialog>
  );
};
